import { createAction, props } from '@ngrx/store';
import {
  VerificationMethod,
  VerificationSource,
} from '../../models/verification.model';

/**
 * Surface a verification challenge to the UI — dispatched by feature
 * code after the originating mutation (broadcast / approve / reject)
 * responds with `202 verification_required`.
 *
 * Carries every field the OTP modal needs to render plus a `source`
 * marker so feature effects can fan out their confirmation step after
 * the code is verified.
 */
export const setVerificationChallenge = createAction(
  '[Verification] Set Challenge',
  props<{
    actionId: string;
    method: VerificationMethod;
    expiresAt: number;
    source: VerificationSource;
  }>()
);

/**
 * Reset the shared verification slice — dispatched when the user closes
 * the OTP modal, when verification succeeds and the confirmation step
 * starts, or when the originating mutation fails outside of the
 * verification step.
 */
export const clearVerificationChallenge = createAction(
  '[Verification] Clear Challenge'
);

/**
 * Submit the user-entered OTP / TOTP code against verification-management.
 * Drives `VerificationEffects.submitVerificationCode$`.
 *
 * Carries the whole challenge it answers — handle *and* flow — so the answer
 * can only ever be addressed to that challenge, and only while the slice still
 * holds it (F-053, F-055).
 */
export const submitVerificationCode = createAction(
  '[Verification] Submit Code',
  props<{ actionId: string; code: string; source: VerificationSource }>()
);

/**
 * Code accepted by verification-management. Feature effects should
 * `ofType(submitVerificationCodeSuccess)` and check both `source` and
 * `actionId` against the challenge they raised before confirming. Only
 * emitted while the slice still holds that exact challenge.
 */
export const submitVerificationCodeSuccess = createAction(
  '[Verification] Submit Code Success',
  props<{ actionId: string; source: VerificationSource }>()
);

/**
 * Code rejected by verification-management (or transport failure).
 * Surfaces a localized error and the remaining attempt count when the
 * backend includes one. `locked` is true when the backend answered
 * `VERIFICATION_LOCKED`: the handle is dead for good and the only way on
 * is to re-fire the originating mutation for a fresh challenge.
 * `codeReused` is true when the code was a right authenticator code another
 * approval already spent: the modal shows when the next one arrives.
 */
export const submitVerificationCodeFailure = createAction(
  '[Verification] Submit Code Failure',
  props<{
    error: string;
    attemptsRemaining: number | null;
    locked?: boolean;
    codeReused?: boolean;
  }>()
);
